import { useState } from 'react';
import { X } from 'lucide-react';
import type { Agent, DayOfWeek, Studio, StudioId } from '@/domain/types';

const inputCls =
  'mt-1 block w-full bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream';
const labelCls = 'block text-[10px] font-inter uppercase tracking-widest text-cream-soft';

const DEFAULT_HOURS: Record<DayOfWeek, { open: string; close: string } | null> = {
  mon: { open: '10:00', close: '18:00' },
  tue: { open: '10:00', close: '18:00' },
  wed: { open: '10:00', close: '18:00' },
  thu: { open: '10:00', close: '18:00' },
  fri: { open: '10:00', close: '18:00' },
  sat: { open: '10:00', close: '18:00' },
  sun: null,
};

interface Props {
  agents: Agent[];
  onCancel: () => void;
  onCreate: (studio: Studio) => void;
}

export default function AddStudioModal({ agents, onCancel, onCreate }: Props) {
  const [name, setName] = useState('');
  const [brand, setBrand] = useState('');
  const [brandedNumber, setBrandedNumber] = useState('');
  const [address, setAddress] = useState('');
  const [postcode, setPostcode] = useState('');
  const [state, setState] = useState('');
  const [managerId, setManagerId] = useState(agents[0]?.id ?? '');

  // managerId is a required Studio field; block create if no agent is selected.
  const canCreate = name.trim() !== '' && brand.trim() !== '' && managerId !== '';

  const handleCreate = () => {
    const studio: Studio = {
      id: `studio-${Date.now()}` as StudioId,
      name: name.trim(),
      brand: brand.trim(),
      brandedNumber: brandedNumber.trim(),
      state: state.trim(),
      address: address.trim(),
      postcode: postcode.trim(),
      lat: 0,
      lon: 0,
      artistCount: 0,
      assignedAgentIds: [],
      operatingHours: { ...DEFAULT_HOURS },
      managerId,
      isBlackBook: false,
    };
    onCreate(studio);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-black/70 p-4 overflow-y-auto"
      data-testid="studio-add-modal"
      onClick={onCancel}
    >
      <div
        className="panel p-6 w-full max-w-xl space-y-4 my-8"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header className="flex items-start justify-between">
          <div>
            <p className="label-small-caps text-gold-bright">New studio</p>
            <h2 className="heading-display text-2xl text-cream mt-1">Add a location</h2>
          </div>
          <button type="button" onClick={onCancel} className="btn-quiet" aria-label="Close">
            <X size={14} />
          </button>
        </header>

        <div className="grid grid-cols-3 gap-3">
          <label className={labelCls}>
            Name
            <input
              className={inputCls}
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              data-testid="studio-add-name"
            />
          </label>
          <label className={labelCls}>
            Brand
            <input
              className={inputCls}
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              data-testid="studio-add-brand"
            />
          </label>
          <label className={labelCls}>
            Branded number
            <input
              className={inputCls}
              value={brandedNumber}
              onChange={(e) => setBrandedNumber(e.target.value)}
            />
          </label>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <label className={labelCls}>
            Address
            <input className={inputCls} value={address} onChange={(e) => setAddress(e.target.value)} />
          </label>
          <label className={labelCls}>
            Postcode
            <input className={inputCls} value={postcode} onChange={(e) => setPostcode(e.target.value)} />
          </label>
          <label className={labelCls}>
            State
            <input className={inputCls} value={state} onChange={(e) => setState(e.target.value)} />
          </label>
        </div>

        <label className={labelCls}>
          Manager
          <select
            className={inputCls}
            value={managerId}
            onChange={(e) => setManagerId(e.target.value)}
            data-testid="studio-add-manager"
          >
            {agents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.firstName} {a.lastName}
              </option>
            ))}
          </select>
        </label>

        <p className="font-serif text-cream-soft text-xs">
          New studios open 10:00–18:00 Mon–Sat (closed Sunday) with no getting-here or prep notes
          yet. Fine-tune everything later via Edit.
        </p>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onCancel} className="btn-quiet text-xs">
            Cancel
          </button>
          <button
            type="button"
            disabled={!canCreate}
            onClick={handleCreate}
            className="btn-primary text-xs"
            data-testid="studio-add-submit"
          >
            Create studio
          </button>
        </div>
      </div>
    </div>
  );
}
